import * as Sentry from "@sentry/react";
import {
  // browserTracingIntegration, // Removed - requires Team/Business plan
  makeFetchTransport,
  replayIntegration,
} from "@sentry/react";
import { isBenignAuthEmptyObjectRejection, isBenignAuthError } from "./authPersistence";
import { isFulfillmentTransientCallableError } from "../../functions/src/common/checkoutFulfillment";
import { isExpectedOrderDeadlineCallableError } from "../../functions/src/common/orderDeadlines";

// Check if user has consented to cookies before initializing Sentry
function hasUserConsentedToCookies(): boolean {
  const consent = localStorage.getItem('cookiesConsent');
  return consent === 'accepted';
}

// Track if Sentry has already been initialized
let sentryInitialized = false;

// Function to check if an error is from a browser extension
function isBrowserExtensionError(event: any): boolean {
  if (!event.exception) {
    return false;
  }

  const error = event.exception.values?.[0];
  if (!error) {
    return false;
  }

  const errorMessage = error.value || '';
  const stacktrace = error.stacktrace?.frames || [];
  
  // Check if error is from browser extensions by filename
  const isExtensionError = stacktrace.some(frame => 
    frame.filename?.includes('/extension/') ||
    frame.filename?.includes('instant-web') ||
    frame.filename?.includes('chrome-extension://') ||
    frame.filename?.includes('moz-extension://') ||
    frame.filename?.includes('safari-extension://') ||
    frame.filename?.includes('edge-extension://') ||
    frame.filename?.includes('extension://')
  );

  // Common extension-related error patterns
  const extensionErrorPatterns = [
    'element.className.split is not a function',
    'instant-web',
    'extension',
    'chrome-extension',
    'moz-extension',
    'safari-extension',
    'edge-extension',
    'Cannot read properties of undefined (reading \'split\')',
    'TypeError: Cannot read properties of undefined',
    'Cannot access before initialization',
    'Script error', // Common with extensions
  ];

  const isExtensionRelatedError = extensionErrorPatterns.some(pattern =>
    errorMessage.includes(pattern)
  );

  return isExtensionError || isExtensionRelatedError;
}

// Some browsers occasionally throw noisy DOM NotFoundError during React commit
// when the DOM is mutated externally (e.g. browser behaviors/extensions/translation).
// Keep this narrow to React commit + removeChild signatures only.
function isKnownReactRemoveChildNotFoundNoise(event: any): boolean {
  const error = event?.exception?.values?.[0];
  if (!error) return false;

  const type = error.type || "";
  const value = (error.value || "").toLowerCase();
  const frames = error.stacktrace?.frames || [];

  const hasRemoveChildFrame = frames.some((frame: any) => frame?.function === "removeChild");
  const hasReactCommitDeletionFrame = frames.some(
    (frame: any) =>
      frame?.function === "commitDeletionEffectsOnFiber" ||
      frame?.function === "recursivelyTraverseMutationEffects" ||
      frame?.function === "commitMutationEffectsOnFiber"
  );
  const isDomNotFound =
    type === "NotFoundError" &&
    (value.includes("object can not be found here") ||
      value.includes("failed to execute 'removechild' on 'node'") ||
      value.includes("node to be removed is not a child of this node"));

  return isDomNotFound && hasRemoveChildFrame && hasReactCommitDeletionFrame;
}

type StackFrameLike = { filename?: string | null };

function hasActionableAppStackFrame(frames: StackFrameLike[]): boolean {
  return frames.some((frame) => {
    const filename = frame?.filename;
    return !!filename && filename !== 'undefined' && /\.(js|ts|tsx|jsx)(\?|$)/.test(filename);
  });
}

function breadcrumbHaystack(breadcrumb: { message?: string | null; data?: Record<string, unknown> }): string {
  const parts: string[] = [];
  if (breadcrumb.message) {
    parts.push(breadcrumb.message);
  }
  if (breadcrumb.data) {
    try {
      parts.push(JSON.stringify(breadcrumb.data));
    } catch {
      // ignore circular structures in breadcrumb payloads
    }
  }
  return parts.join(' ').toLowerCase();
}

function hasGoogleTranslateBreadcrumb(event: Sentry.ErrorEvent): boolean {
  const breadcrumbs = event.breadcrumbs ?? [];
  return breadcrumbs.some((breadcrumb) => {
    const text = breadcrumbHaystack(breadcrumb);
    return text.includes('translated-ltr')
      || text.includes('translated-rtl')
      || text.includes('font > font')
      || text.includes('goog-te-');
  });
}

function hasOnlyNonBundleStackFrames(frames: StackFrameLike[]): boolean {
  if (frames.length === 0) {
    return true;
  }
  return frames.every((frame) => {
    const filename = frame?.filename || '';
    return !filename
      || filename === 'undefined'
      || ((filename.endsWith('/order/') || filename.endsWith('/admin/')) && !/\.(js|ts|tsx|jsx)(\?|$)/.test(filename));
  });
}

/**
 * LUNCHBOX-N / LUNCHBOX-5A: Chrome/Safari page translation mutates the DOM (font wrappers, translated-ltr).
 * Session replay / instrumentation can recurse until RangeError with no actionable app stack.
 * 845 events / 1 user — not a Lunchbox logic bug.
 */
function isGoogleTranslateStackOverflowNoise(event: Sentry.ErrorEvent): boolean {
  const error = event.exception?.values?.[0];
  if (!error) {
    return false;
  }
  const type = (error.type || '').toLowerCase();
  const value = (error.value || '').toLowerCase();
  if (type !== 'rangeerror' || !value.includes('maximum call stack size exceeded')) {
    return false;
  }
  const frames = error.stacktrace?.frames ?? [];
  if (hasActionableAppStackFrame(frames)) {
    return false;
  }
  return hasGoogleTranslateBreadcrumb(event) || hasOnlyNonBundleStackFrames(frames);
}

/** LUNCHBOX-5V: iOS Mail in-app browser (mailapp/WKWebView) injects reader scripts calling getAbstract/getFirstImage — not our code. */
function getEventUserAgent(event: Sentry.ErrorEvent): string {
  const headers = event.request?.headers;
  if (!Array.isArray(headers)) {
    return '';
  }
  const uaEntry = headers.find(([key]) => typeof key === 'string' && key.toLowerCase() === 'user-agent');
  return (uaEntry?.[1] || '').toLowerCase();
}

function isInAppBrowserReaderNoise(event: Sentry.ErrorEvent): boolean {
  const error = event.exception?.values?.[0];
  if (!error || (error.type || '') !== 'ReferenceError') {
    return false;
  }
  const value = (error.value || '').toLowerCase();
  const readerRefMissing = /can't find variable: get(abstract|firstimage|summary|article|content|metadata)/.test(value);
  const ua = getEventUserAgent(event);
  const inAppMail = ua.includes('mailapp') || ua.includes('wkwebview');
  if (!readerRefMissing && !inAppMail) {
    return false;
  }
  const frames = error.stacktrace?.frames ?? [];
  if (hasActionableAppStackFrame(frames)) {
    return false;
  }
  return hasOnlyNonBundleStackFrames(frames);
}

function isBenignFirebaseAuthSentryEvent(event: any, hint: Sentry.EventHint): boolean {
  if (isBenignAuthError(hint.originalException)) {
    return true;
  }
  if (isBenignAuthLookupEmptyRejection(event, hint)) {
    return true;
  }
  const errorMessage = event?.exception?.values?.[0]?.value || '';
  return isBenignAuthError(new Error(errorMessage));
}

/** LUNCHBOX-5T: `{}` rejection right after failed identitytoolkit accounts:lookup on auth startup. */
function isBenignAuthLookupEmptyRejection(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  if (!isUnhandledRejectionEvent(event)) {
    return false;
  }
  if (!isBenignAuthEmptyObjectRejection(hint.originalException)) {
    return false;
  }
  const breadcrumbs = event.breadcrumbs ?? [];
  return breadcrumbs.some((breadcrumb) => {
    const text = breadcrumbHaystack(breadcrumb);
    return text.includes('identitytoolkit.googleapis.com') && text.includes('accounts:lookup');
  });
}

function isUnhandledRejectionEvent(event: Sentry.ErrorEvent): boolean {
  const mechanism = event.exception?.values?.[0]?.mechanism;
  return mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection' || mechanism?.handled === false;
}

/** Serialize a promise rejection reason for Sentry context (non-Error values are otherwise opaque). */
function describeRejectionValue(reason: unknown): Record<string, string | boolean | string[] | null> {
  if (reason === null) {
    return { type: 'null', summary: 'null' };
  }
  if (reason === undefined) {
    return { type: 'undefined', summary: 'undefined' };
  }
  if (reason instanceof Error) {
    return {
      type: 'error',
      summary: reason.message || reason.name || 'Error',
      errorName: reason.name,
      errorMessage: reason.message,
    };
  }
  if (typeof reason === 'function') {
    const functionName = reason.name || 'anonymous';
    return { type: 'function', summary: `function ${functionName}`, functionName };
  }
  if (typeof reason === 'string') {
    const stringValue = reason.length > 200 ? `${reason.slice(0, 200)}…` : reason;
    return { type: 'string', summary: stringValue, stringValue };
  }
  if (typeof reason === 'object') {
    const objectKeys = Object.keys(reason as object).slice(0, 20);
    if (reason instanceof Event) {
      return {
        type: 'event',
        summary: `Event type=${reason.type}`,
        eventType: reason.type,
        isTrusted: reason.isTrusted,
        objectKeys,
      };
    }
    let serialized = '';
    try {
      serialized = JSON.stringify(reason);
      if (serialized.length > 300) {
        serialized = `${serialized.slice(0, 300)}…`;
      }
    } catch {
      serialized = '[unserializable]';
    }
    return {
      type: 'object',
      summary: `object keys=[${objectKeys.join(',')}]`,
      objectKeys,
      serialized,
    };
  }
  return { type: typeof reason, summary: String(reason) };
}

const LEGACY_BREVO_EMPTY_NAME_MESSAGES = new Set(['First name is required', 'Last name is required']);

function isLegacyBrevoEmptyNameMessage(text: string): boolean {
  return LEGACY_BREVO_EMPTY_NAME_MESSAGES.has(text.trim());
}

/** LUNCHBOX-61: legacy profile debounce called Brevo sync with empty names (pre–ProfileEditableField). */
function isLegacyBrevoEmptyNameCallableNoise(error: unknown): boolean {
  if (error instanceof Error && isLegacyBrevoEmptyNameMessage(error.message)) {
    if (typeof error === 'object' && error !== null && 'code' in error) {
      const code = String((error as { code?: unknown }).code || '').replace(/^functions\//, '');
      if (code && code !== 'invalid-argument') {
        return false;
      }
    }
    return true;
  }
  if (typeof error !== 'object' || error === null || !('message' in error)) {
    return false;
  }
  const message = String((error as { message?: unknown }).message).trim();
  if (!isLegacyBrevoEmptyNameMessage(message)) {
    return false;
  }
  if ('code' in error) {
    const code = String((error as { code?: unknown }).code || '').replace(/^functions\//, '');
    if (code && code !== 'invalid-argument') {
      return false;
    }
  }
  return true;
}

const EXPECTED_EMAIL_UPDATE_MESSAGES = new Set([
  'New email must be different from current email',
  'Invalid email format',
  'Email address is already in use by another account',
  'User has no current email address',
]);

function normalizeCallableErrorMessage(message: string): string {
  return message.replace(/\s*\[\d+\]\s*$/, '').trim();
}

function isExpectedEmailUpdateMessage(text: string): boolean {
  return EXPECTED_EMAIL_UPDATE_MESSAGES.has(normalizeCallableErrorMessage(text));
}

function callableFunctionsErrorCode(error: object): string {
  return String((error as { code?: unknown }).code || '').replace(/^functions\//, '');
}

/** LUNCHBOX-65: parent submitted validation failures from `updateUserEmail` (not app bugs). */
export function isExpectedEmailUpdateCallableError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('message' in error)) {
    return false;
  }
  const message = String((error as { message?: unknown }).message);
  if (!isExpectedEmailUpdateMessage(message)) {
    return false;
  }
  const code = callableFunctionsErrorCode(error as object);
  return code === 'invalid-argument' || code === 'already-exists';
}

function isExpectedEmailUpdateSentryEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  if (isExpectedEmailUpdateCallableError(hint.originalException)) {
    return true;
  }
  const entry = event.exception?.values?.[0];
  if (!entry) {
    return false;
  }
  const value = (entry.value || '').trim();
  if (!isExpectedEmailUpdateMessage(value)) {
    return false;
  }
  const type = entry.type || '';
  return type === 'FirebaseError' || type === 'Error' || type === '';
}

/** Match Sentry payload when `hint.originalException` is missing (global unhandledrejection → FirebaseError). */
function isLegacyBrevoEmptyNameSentryEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  if (isLegacyBrevoEmptyNameCallableNoise(hint.originalException)) {
    return true;
  }
  const entry = event.exception?.values?.[0];
  if (!entry) {
    return false;
  }
  const value = (entry.value || '').trim();
  if (!isLegacyBrevoEmptyNameMessage(value)) {
    return false;
  }
  const type = entry.type || '';
  return type === 'FirebaseError' || type === 'Error' || type === '';
}

/**
 * LUNCHBOX-58: minified SDK helper name when a function/object is rejected (Sentry bundle `isString` → `Ea`).
 * No app stack — safe to drop after logging locally.
 */
function isCrypticMinifiedUnhandledRejectionNoise(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  if (!isUnhandledRejectionEvent(event)) {
    return false;
  }
  const message = event.exception?.values?.[0]?.value || '';
  const original = hint.originalException;
  if (message !== 'Ea') {
    return false;
  }
  return typeof original === 'function' || (original instanceof Error && original.message === 'Ea');
}

/** Attach rejection metadata so future non-Error / cryptic events are debuggable in Sentry. */
function enrichUnhandledRejectionEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent {
  const original = hint.originalException;
  if (!isUnhandledRejectionEvent(event) && original instanceof Error) {
    return event;
  }

  const rejection = describeRejectionValue(original);
  event.contexts = { ...event.contexts, unhandled_rejection: rejection };
  event.tags = { ...event.tags, rejection_type: rejection.type as string };

  const entry = event.exception?.values?.[0];
  if (!entry) {
    return event;
  }

  const shouldReplaceMessage =
    !(original instanceof Error)
    || !entry.value
    || entry.value === 'Ea'
    || entry.value.startsWith('Non-Error promise rejection captured');
  if (shouldReplaceMessage) {
    entry.value = `Unhandled rejection (${rejection.summary})`;
  }

  return event;
}

// Detect environment
function getEnvironment(): string {
  // Check if we're in development (localhost or emulator)
  if (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.port === '5000') {
    return 'development';
  }
  return 'production';
}

// Initialize Sentry only if user has consented
export function initSentry() {
  if (!hasUserConsentedToCookies()) {
    return;
  }

  // Prevent multiple initializations
  if (sentryInitialized) {
    return;
  }

  const environment = getEnvironment();

  try {
    Sentry.init({
      dsn: "https://d49d7d743f1eb505ffba8bcbad32335e@o4509588044840960.ingest.de.sentry.io/4509588290863184",

      environment: environment,
      release: process.env.SENTRY_RELEASE,

      // We no longer need debug mode. This will silence the Sentry logger.
      // debug: environment === 'development',

      // Force enable to ensure it's not disabled
      enabled: true,

      // Explicitly set the transport to ensure it's bundled correctly.
      transport: makeFetchTransport,

      // Add standard integrations (removed performance monitoring for Developer plan)
      integrations: [
        // browserTracingIntegration(), // Removed - requires Team/Business plan
        replayIntegration(),
      ],

      // Performance Monitoring - disabled for Developer plan
      // tracesSampleRate: environment === 'production' ? 0.25 : 0,

      // Session Replay - only in production
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 0,
      replaysOnErrorSampleRate: environment === 'production' ? 1.0 : 0,

      // Only send events to Sentry if in the production environment.
      beforeSend(event, hint) {
        if (environment !== 'production') {
          return null;
        }

        // Filter out browser extension errors
        if (isBrowserExtensionError(event)) {
          const errorMessage = event.exception?.values?.[0]?.value || 'Unknown extension error';
          console.log('🔇 Filtered out browser extension error:', errorMessage);
          return null;
        }

        // iOS Mail / in-app WKWebView reader scripts (getAbstract, getFirstImage, …)
        if (isInAppBrowserReaderNoise(event)) {
          console.log('🔇 Filtered in-app browser reader ReferenceError noise');
          return null;
        }

        // Filter known noisy React DOM removeChild NotFoundError signatures
        if (isKnownReactRemoveChildNotFoundNoise(event)) {
          console.log('🔇 Filtered known React removeChild NotFoundError noise');
          return null;
        }

        // Firebase Auth IndexedDB persistence / transient network while tab hidden or offline
        if (isBenignFirebaseAuthSentryEvent(event, hint)) {
          console.log('🔇 Filtered benign Firebase Auth error');
          return null;
        }

        // Expected order deadline rejections from Cloud Functions (failed-precondition user messages)
        if (isExpectedOrderDeadlineCallableError(hint.originalException)) {
          console.log('🔇 Filtered expected order deadline callable rejection');
          return null;
        }

        // Legacy Brevo name sync on empty profile fields (LUNCHBOX-61)
        if (isLegacyBrevoEmptyNameSentryEvent(event, hint)) {
          console.log('🔇 Filtered legacy Brevo empty name callable rejection');
          return null;
        }

        // Profile email change validation (LUNCHBOX-65)
        if (isExpectedEmailUpdateSentryEvent(event, hint)) {
          console.log('🔇 Filtered expected email update callable rejection');
          return null;
        }

        // Post-Viva webhook timing / wallet mutex — retried ~80s then shown in UI (LUNCHBOX-5W)
        if (isFulfillmentTransientCallableError(hint.originalException)) {
          console.log('🔇 Filtered expected fulfillment transient callable rejection');
          return null;
        }

        // Minified non-Error rejections (e.g. Sentry SDK isString → "Ea") — no actionable app stack
        if (isCrypticMinifiedUnhandledRejectionNoise(event, hint)) {
          console.log('🔇 Filtered cryptic minified unhandled rejection:', event.exception?.values?.[0]?.value);
          return null;
        }

        // Google Translate / injected DOM stack overflow on mobile (LUNCHBOX-N)
        if (isGoogleTranslateStackOverflowNoise(event)) {
          console.log('🔇 Filtered Google Translate / no-stack RangeError noise');
          return null;
        }

        return enrichUnhandledRejectionEvent(event, hint);
      },
    });

    sentryInitialized = true;

    // Set basic tags
    Sentry.setTag('environment', environment);

    // Add global test function for production error testing
    (window as any).testSentryError = () => {
      if (environment !== 'production') {
        console.log('⚠️ Sentry test error blocked: Only available in production environment');
        return 'Test error blocked - only works in production';
      }

      console.log('🔥 Testing Sentry error capture in production...');
      const error = new Error('PRODUCTION TEST ERROR - Sentry error capture verification (intentional test)');

      Sentry.withScope((scope) => {
        scope.setTag('test_type', 'production_verification');
        scope.setTag('test_source', 'console_command');
        scope.setLevel('error');
        scope.setContext('production_test_context', {
          test_name: 'Production Sentry Verification',
          timestamp: new Date().toISOString(),
          url: window.location.href,
          environment: environment,
          userAgent: navigator.userAgent,
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          referrer: document.referrer || 'direct'
        });

        // Add user context if available
        try {
          if ((window as any).firebase?.auth?.()?.currentUser) {
            const user = (window as any).firebase.auth().currentUser;
            scope.setUser({
              id: user.uid,
              email: user.email || 'no-email'
            });
          }
        } catch (e) {
          // Ignore if firebase auth is not available
        }

        Sentry.captureException(error);
      });

      console.log('✅ Production test error sent to Sentry. Check your dashboard in 1-2 minutes.');
      console.log('🔗 Dashboard: https://lunchbox-h1.sentry.io/');
      return 'Production Sentry test error sent!';
    };

    // Add test function for extension error filtering
    (window as any).testExtensionErrorFilter = () => {
      if (environment !== 'production') {
        console.log('⚠️ Extension error filter test blocked: Only available in production environment');
        return 'Test blocked - only works in production';
      }

      console.log('🧪 Testing extension error filtering...');
      
      // Simulate the exact error from the instant-web extension
      const extensionError = new Error('TypeError: element.className.split is not a function');
      extensionError.stack = `TypeError: element.className.split is not a function
    at getSelector (/instant-web/extension/instantweb.min.js:1:724)
    at isContainer (/instant-web/extension/instantweb.min.js:1:856)
    at ? (/instant-web/extension/instantweb.min.js:1:1268)
    at sentryWrapped (../../node_modules/@sentry/browser/src/helpers.ts:117:17)`;

      Sentry.withScope((scope) => {
        scope.setTag('test_type', 'extension_error_filter');
        scope.setTag('test_source', 'console_command');
        scope.setLevel('error');
        scope.setContext('extension_test_context', {
          test_name: 'Extension Error Filter Test',
          timestamp: new Date().toISOString(),
          expected_behavior: 'Should be filtered out and not sent to Sentry'
        });

        Sentry.captureException(extensionError);
      });

      console.log('✅ Extension error filter test completed. This error should be filtered out and NOT appear in Sentry dashboard.');
      return 'Extension error filter test completed';
    };

    if (environment === 'production') {
      console.log('🚀 Sentry initialized for production.');
    } else {
      console.log('🔧 Sentry initialized for development (errors will not be sent to dashboard).');
    }

  } catch (error) {
    console.error('Error initializing Sentry:', error);
  }
}

// Helper function to check if Sentry is active
export function isSentryActive(): boolean {
  return Sentry.getClient() !== undefined && hasUserConsentedToCookies();
}

// Wrapper functions that only execute if consent is given
export function captureException(error: Error, context?: any) {
  if (isSentryActive())
    return Sentry.captureException(error, context);
}

export function setUserContext(user: any) {
  if (isSentryActive())
    Sentry.setUser(user);
}

// Performance monitoring removed for Developer plan
// export function startSpan(spanOptions: any, callback: (span: any) => any) {
//   if (isSentryActive()) {
//     return Sentry.startSpan(spanOptions, callback);
//   } else {
//     // Execute callback without span if Sentry is not active
//     return callback(null);
//   }
// }

// Get the Sentry logger
export const { logger } = Sentry;

// Helper function to capture messages with context
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) {
  if (isSentryActive()) {
    const eventId = Sentry.withScope((scope) => {
      if (context) {
        scope.setContext("message_context", context);
      }
      return Sentry.captureMessage(message, level);
    });
    return eventId;
  }
}

// Helper function to add custom tags
export function addTag(key: string, value: string) {
  if (isSentryActive()) {
    Sentry.setTag(key, value);
  }
}

export default Sentry; 